import {IntlProvider} from 'react-intl';
import {MessageFormatElement} from 'intl-messageformat-parser';
import {Visibility} from '@stripe-internal/conversations-ui';
import {ScreenProvider} from './ScreenProvider';
import {ConfigProvider} from './ConfigProvider';
import {ClientProvider} from './ClientProvider';
import {AppThemeProvider} from './AppThemeProvider';
import {AppWidgetProvider} from './AppWidgetProvider';
import {InlineDataProvider} from './InlineDataProvider';
import {ObservabilityProvider} from './ObservabilityProvider';
import {ProfileProvider} from './ProfileProvider';
import {ParentMessageBusProvider} from './ParentMessageBusProvider';
import {ConversationsUIFeatureFlagsProvider} from './ConversationsUIFeatureFlagsProvider';

type Props = {
  locale?: string;
  messages?: Record<string, string> | Record<string, MessageFormatElement[]>;
  initialVisibility?: Visibility;
  children: JSX.Element;
};

export const AppProviders = ({
  locale = 'en-US',
  messages,
  initialVisibility,
  children,
}: Props) => {
  return (
    <IntlProvider defaultLocale="en-US" locale={locale} messages={messages}>
      <ConfigProvider>
        <ParentMessageBusProvider>
          <ObservabilityProvider>
            <AppThemeProvider>
              <AppWidgetProvider initialVisibility={initialVisibility}>
                <ScreenProvider>
                  {/*
                    Anything that can be outside the ProfileProvider should be,
                    so it doesn't get re-rendered and potentially lose its state
                    when the profile is fetched asynchronously. The ProfileProvider
                    re-provides the config, but that could cause some provider
                    state to get reset.
                  */}
                  <ProfileProvider>
                    {/* Providers that depend on profile data inside ProfileProvider */}
                    <ConversationsUIFeatureFlagsProvider>
                      <ClientProvider>
                        <InlineDataProvider>{children}</InlineDataProvider>
                      </ClientProvider>
                    </ConversationsUIFeatureFlagsProvider>
                  </ProfileProvider>
                </ScreenProvider>
              </AppWidgetProvider>
            </AppThemeProvider>
          </ObservabilityProvider>
        </ParentMessageBusProvider>
      </ConfigProvider>
    </IntlProvider>
  );
};
